import {
  collection,
  doc,
  getDoc,
  getFirestore,
  limit,
  onSnapshot,
  query,
  updateDoc,
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SenderForm from '../SenderForm/SenderForm';
import './styles.css';

// Стилі для компонентів
const styles = {
  deliveryContainer: {
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    backgroundColor: '#f9f9f905',
  },
  errorBanner: {
    backgroundColor: '#ffdddd',
    borderLeft: '4px solid #f44336',
    padding: '12px',
    marginBottom: '16px',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  errorCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#666',
  },
  loadingIndicator: {
    textAlign: 'center',
    margin: '20px 0',
    color: '#666',
  },
  noOrdersMessage: {
    textAlign: 'center',
    margin: '48px 0',
    color: '#666',
    fontSize: '18px',
  },
  deliveryList: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: '24px',
    padding: '16px',
    justifyContent: 'flex-start',
  },
  loadMoreBtn: {
    margin: '24px auto',
    padding: '10px 20px',
    backgroundColor: '#f0f0f0',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s',
  },
  loadMoreBtnHover: {
    backgroundColor: '#e0e0e0',
  },
  loadMoreBtnDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  deliveryItem: {
    backgroundColor: '#323232',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '16px',
    height: 'fit-content',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  deliveryItemHeader: {
    marginBottom: '12px',
  },
  deliveryItemTitle: {
    fontWeight: 'bold',
    fontSize: '16px',
    marginBottom: '4px',
  },
  deliveryItemStatus: {
    color: '#dfdfdf',
    fontSize: '14px',
  },
  deliveryItemProducts: {
    margin: '12px 0',
  },
  deliveryItemProduct: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
  },
  withBorder: {
    borderBottom: '1px solid #121212',
  },
  lastItem: {
    borderBottom: '2px solid #3e3e3e',
  },
  productName: {
    fontWeight: 500,
  },
  productCount: {
    color: '#666',
  },
  deliveryItemFooter: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '10px',
  },
  deliveryItemPromo: {
    fontSize: '14px',
    color: '#666',
  },
  deliveryItemPrice: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  priceOriginal: {
    textDecoration: 'line-through',
    color: '#888',
  },
  priceDiscounted: {
    fontWeight: 'bold',
    color: '#e74c3c',
  },
  hideOrderBtn: {
    padding: '8px 12px',
    backgroundColor: '#ff6b35',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s',
    marginTop: '8px',
    alignSelf: 'flex-start',
  },
  hideOrderBtnHover: {
    backgroundColor: '#e05a2b',
  },
  hideOrderBtnDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    width: '90%',
    maxWidth: '400px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  btnCancel: {
    padding: '8px 16px',
    backgroundColor: '#4caf50',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  btnConfirm: {
    padding: '8px 16px',
    backgroundColor: '#ff6b35',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  filterContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    alignItems: 'center',
  },
  filterButton: {
    padding: '8px 16px',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: '#ddd',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
    color: '#666',
  },
  filterButtonActive: {
    borderColor: '#2196f3',
    backgroundColor: '#2196f3',
    color: '#fff',
  },
  filterLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginRight: '8px',
  },
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    // Якщо клік по backdrop (не по модальному вікну), то скасовуємо
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div style={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div style={styles.modalContent}>
        <h3>Підтвердження</h3>
        <p>{message}</p>
        <div style={styles.modalActions}>
          <button style={styles.btnCancel} onClick={onCancel}>
            Скасувати
          </button>
          <button style={styles.btnConfirm} onClick={onConfirm}>
            Підтвердити
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Delivery Component
const Delivery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deliveryFilter, setDeliveryFilter] = useState('all'); // 'all', 'delivered', 'pending'
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    orderId: null,
  });
  const [pageSize, setPageSize] = useState(10);

  const db = getFirestore();


  const calculatePriceWithDiscount = useCallback((cart, discount) => {
    const totalPrice = cart.reduce((acc, p) => acc + p.item.price * p.count, 0);

    if (discount && discount.percent) {
      const discountAmount = (totalPrice * discount.percent) / 100;
      return totalPrice - discountAmount;
    }

    return totalPrice;
  }, []);

  const itemsWithPrices = useMemo(() => {
    const allItemsWithPrices = items.map((item) => ({
      ...item,
      originalPrice: item.cart.reduce(
        (acc, p) => acc + p.item.price * p.count,
        0
      ),
      discountedPrice: calculatePriceWithDiscount(item.cart, item.discount),
    }));

    let filteredItems;
    switch (deliveryFilter) {
      case 'delivered':
        filteredItems = allItemsWithPrices.filter(item =>
          item.delivered === true || item.delivered === 'true' || item.delivered === 1
        );
        break;
      case 'pending':
        filteredItems = allItemsWithPrices.filter(item =>
          item.delivered !== true && item.delivered !== 'true' && item.delivered !== 1
        );
        break;
      case 'all':
      default:
        filteredItems = allItemsWithPrices;
        // Сортування для "Всі замовлення": не доставлені зверху
        filteredItems = filteredItems.sort((a, b) => {
          const aDelivered = a.delivered === true || a.delivered === 'true' || a.delivered === 1;
          const bDelivered = b.delivered === true || b.delivered === 'true' || b.delivered === 1;

          // Не доставлені (false) мають бути зверху, тому повертаємо -1 якщо a не доставлено, а b доставлено
          if (!aDelivered && bDelivered) return -1;
          if (aDelivered && !bDelivered) return 1;
          return 0; // Якщо обидва в одному стані, залишаємо як є
        });
        break;
    }

    return filteredItems;
  }, [items, calculatePriceWithDiscount, deliveryFilter]);

  const filterCounts = useMemo(() => {
    const allItemsWithPrices = items.map((item) => ({
      ...item,
      originalPrice: item.cart.reduce(
        (acc, p) => acc + p.item.price * p.count,
        0
      ),
      discountedPrice: calculatePriceWithDiscount(item.cart, item.discount),
    }));

    const delivered = allItemsWithPrices.filter(item =>
      item.delivered === true || item.delivered === 'true' || item.delivered === 1
    ).length;

    const pending = allItemsWithPrices.filter(item =>
      item.delivered !== true && item.delivered !== 'true' && item.delivered !== 1
    ).length;

    return {
      all: allItemsWithPrices.length,
      delivered,
      pending
    };
  }, [items, calculatePriceWithDiscount]);

  // Функція для перевірки та синхронізації статусу оплати
  const checkAndSyncPaymentStatus = useCallback(
    async (deliveryItem) => {
      try {
        if (deliveryItem.paymentStatus === 'completed') {
          return deliveryItem;
        }

        if (!deliveryItem.paymentOrderId) {
          return deliveryItem;
        }

        const paymentRef = doc(db, 'payments', deliveryItem.paymentOrderId);
        const paymentSnapshot = await getDoc(paymentRef);

        if (paymentSnapshot.exists()) {
          const paymentData = paymentSnapshot.data();

          if (
            paymentData.status &&
            paymentData.status !== deliveryItem.paymentStatus
          ) {
            const deliveryRef = doc(db, 'delivery', deliveryItem.id);
            await updateDoc(deliveryRef, {
              paymentStatus: paymentData.status,
              paymentStatusUpdateTimestamp: new Date(),
            });

            return {
              ...deliveryItem,
              paymentStatus: paymentData.status,
            };
          }
        }

        // Якщо платіж не знайдено або статус не змінився, повертаємо оригінальний об'єкт
        return deliveryItem;
      } catch (error) {
        console.error('Error checking payment status:', error);
        setError(`Помилка перевірки статусу оплати: ${error.message}`);
        return deliveryItem;
      }
    },
    [db, setError]
  );

  // Nova Post API interaction - would be better in a backend service
  const fetchOrderStatusFromNovaPost = useCallback(
    async (orderNumber, recipientPhone) => {
      try {
        // NOTE: API keys should be handled on a backend service
        // This is just for demonstration
        const apiKey = process.env.REACT_APP_NOVA_POST_KEY;
        const apiUrl = 'https://api.novaposhta.ua/v2.0/json/';

        const requestBody = {
          apiKey,
          modelName: 'TrackingDocumentGeneral',
          calledMethod: 'getStatusDocuments',
          methodProperties: {
            Documents: [
              {
                DocumentNumber: orderNumber,
                Phone: recipientPhone || '',
              },
            ],
          },
        };

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data.length > 0) {
          const statusInfo = data.data[0];
          const statusCode = statusInfo.StatusCode;
          const statusDescription = statusInfo.Status;

          switch (statusCode) {
            case '9':
              return 'delivered';
            default:
              return statusDescription;
          }
        } else {
          const errorMessage = data.errors?.join(', ') || 'Unknown error';
          setError(`Error fetching order status: ${errorMessage}`);
          return null;
        }
      } catch (error) {
        setError(`Error fetching order status: ${error.message}`);
        return null;
      }
    },
    [setError]
  );

  // Update order status in Firestore
  const updateOrderStatus = useCallback(
    async (orderId, status, isDelivered = false) => {
      try {
        setLoading(true);
        const orderRef = doc(db, 'delivery', orderId);
        await updateDoc(orderRef, {
          status,
          statusUpdateTimestamp: new Date(),
          delivered: isDelivered || status === 'delivered',
        });
      } catch (error) {
        setError(`Error updating order status: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [db, setLoading, setError]
  );

  // Process orders to check and update their status
  const processOrders = useCallback(
    async (orders) => {
      setLoading(true);
      setError(null);

      try {
        let updatedOrders = [];
        const now = new Date();

        for (const order of orders) {
          const { id, novaPost, status, statusUpdateTimestamp, delivered } =
            order;

          // Перевірка та синхронізація статусу оплати
          const orderWithPaymentStatus = await checkAndSyncPaymentStatus(order);

          if (delivered) {
            updatedOrders.push(orderWithPaymentStatus);
            continue;
          }

          let shouldUpdateStatus = false;

          // Check if we need to update the status
          if (!status || !statusUpdateTimestamp) {
            shouldUpdateStatus = true;
          } else {
            const lastUpdate = new Date(statusUpdateTimestamp);
            const timeDiff = (now - lastUpdate) / (1000 * 60 * 60);
            if (timeDiff > 1) {
              shouldUpdateStatus = true;
            }
          }

          if (shouldUpdateStatus) {
            const newStatus = await fetchOrderStatusFromNovaPost(
              novaPost.IntDocNumber
            );

            if (newStatus) {
              await updateOrderStatus(id, newStatus, newStatus === 'delivered');

              orderWithPaymentStatus.status = newStatus;
              orderWithPaymentStatus.delivered = newStatus === 'delivered';
            }
          }

          updatedOrders.push(orderWithPaymentStatus);
        }

        setItems(updatedOrders);
      } catch (err) {
        setError(`Failed to process orders: ${err.message}`);
      } finally {
        setLoading(false);
      }
    },
    [
      fetchOrderStatusFromNovaPost,
      updateOrderStatus,
      checkAndSyncPaymentStatus,
      setLoading,
      setError,
    ]
  );

  // Subscribe to Firestore for active orders
  useEffect(() => {
    const q = query(
      collection(db, 'delivery'),
      limit(pageSize * 2) // Fetch more to account for filtering
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newItems = snapshot.docs
          .filter((doc) => doc.id !== 'senderData')
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((item) => {
            return item.cart && item.cart.length > 0 && item.recipient && item.novaPost;
          });

        // Limit results after filtering
        const limitedItems = newItems.slice(0, pageSize);
        processOrders(limitedItems);
      },
      (error) => {
        setError(`Error listening to delivery: ${error.message}`);
      }
    );

    return () => unsubscribe();
  }, [db, processOrders, pageSize]);

  // Handle marking an order as delivered
  const markAsDelivered = useCallback(
    async (orderId) => {
      try {
        await updateOrderStatus(orderId, 'delivered', true);
        setConfirmModal({ open: false, orderId: null });
      } catch (error) {
        setError(`Error marking order as delivered: ${error.message}`);
      }
    },
    [updateOrderStatus, setError]
  );

  // Handle opening confirmation modal
  const handleHideOrder = useCallback((orderId) => {
    setConfirmModal({
      open: true,
      orderId,
    });
  }, []);

  // Handle closing confirmation modal
  const handleCancelHide = useCallback(() => {
    setConfirmModal({
      open: false,
      orderId: null,
    });
  }, []);

  // Handle confirming order hide
  const handleConfirmHide = useCallback(() => {
    if (confirmModal.orderId) {
      markAsDelivered(confirmModal.orderId);
    }
  }, [confirmModal.orderId, markAsDelivered]);

  return (
    <>
      <SenderForm />
      <div style={styles.deliveryContainer}>
        {/* Error message display */}
        {error && (
          <div style={styles.errorBanner}>
            {error}
            <button style={styles.errorCloseBtn} onClick={() => setError(null)}>
              ×
            </button>
          </div>
        )}

        {/* Delivery Filter */}
        <div style={styles.filterContainer}>
          <span style={styles.filterLabel}>Фільтр:</span>
          <button
            style={{
              ...styles.filterButton,
              ...(deliveryFilter === 'all' ? styles.filterButtonActive : {})
            }}
            onClick={() => setDeliveryFilter('all')}
          >
            Всі замовлення ({filterCounts.all})
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(deliveryFilter === 'pending' ? styles.filterButtonActive : {})
            }}
            onClick={() => setDeliveryFilter('pending')}
          >
            🚚 Не доставлені ({filterCounts.pending})
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(deliveryFilter === 'delivered' ? styles.filterButtonActive : {})
            }}
            onClick={() => setDeliveryFilter('delivered')}
          >
            ✅ Доставлені ({filterCounts.delivered})
          </button>
        </div>

        {/* Loading indicator */}
        {loading && <div style={styles.loadingIndicator}>Завантаження...</div>}

        {/* Empty state message */}
        {!loading && itemsWithPrices.length === 0 && (
          <div style={styles.noOrdersMessage}>
            {deliveryFilter === 'all' && 'Немає замовлень для відображення'}
            {deliveryFilter === 'pending' && 'Немає не доставлених замовлень'}
            {deliveryFilter === 'delivered' && 'Немає доставлених замовлень'}
          </div>
        )}

        {/* Orders list */}
        <div style={styles.deliveryList}>
          {itemsWithPrices.map((item) => (
            <RenderItem
              key={item.id}
              item={item}
              loading={loading}
              handleHideOrder={handleHideOrder}
            />
          ))}
        </div>

        {/* Load more button */}
        {itemsWithPrices.length >= pageSize && (
          <button
            style={loading ? styles.loadMoreBtnDisabled : styles.loadMoreBtn}
            onClick={() => setPageSize((prevSize) => prevSize + 10)}
            disabled={loading}
          >
            Завантажити ще
          </button>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmModal.open}
          message='Ви впевнені, що хочете позначити це замовлення як доставлено?'
          onConfirm={handleConfirmHide}
          onCancel={handleCancelHide}
        />
      </div>
    </>
  );
};

const RenderItem = ({ item, loading, handleHideOrder }) => {
  return (
    <div key={item.id} style={styles.deliveryItem}>
      <div style={styles.deliveryItemHeader}>
        <div style={styles.deliveryItemTitle}>
          Номер накладної: {item.novaPost.IntDocNumber}
        </div>
        <div style={styles.deliveryItemTitle}>
          {` Отримувач: ${item?.recipient?.name} ${item.recipient?.surname}`}
        </div>
        <div style={styles.deliveryItemTitle}>
          {`Телефон: ${item?.recipient?.phone}`}
        </div>
        <div style={styles.deliveryItemTitle}>
          {`Нова пошта: ${item?.recipient?.warehouse?.CityDescription || item?.recipient?.city?.Description || ''}, ${item?.recipient?.warehouse?.Description || ''}`}
        </div>
        <div style={styles.deliveryItemStatus}>
          Статус: {item.status || 'Невідомо'}
        </div>
        <div style={{
          ...styles.deliveryItemStatus,
          color: item.delivered ? '#4caf50' : '#ff9800',
          fontWeight: 'bold'
        }}>
          {item.delivered ? '✅ Доставлено' : '🚚 В процесі доставки'}
        </div>
      </div>

      {/* Products list */}
      <div style={styles.deliveryItemProducts}>
        {item.cart.map((p, index) => (
          <div
            key={`${item.id}-${p.item.name}`}
            style={{
              ...styles.deliveryItemProduct,
              ...(index !== item.cart.length - 1
                ? styles.withBorder
                : styles.lastItem),
            }}
          >
            <div style={styles.productName}>{p.item.name}</div>
            <div style={styles.productCount}>{p.count} шт</div>
          </div>
        ))}
      </div>

      {/* Order footer with price and actions */}
      <div style={styles.deliveryItemFooter}>
        {!!item.discount?.percent && (
          <div style={styles.deliveryItemPromo}>
            {!item.discount
              ? 'Без промокоду'
              : `Промокод: "${item.discount?.name}" - ${item.discount?.percent}%`}
          </div>
        )}

        <div style={styles.deliveryItemPrice}>
          <div>Ціна:</div>
          {item.discount && item.discount && item.discount.percent > 0 ? (
            <>
              <div style={styles.priceOriginal}>
                {item.originalPrice.toFixed(2)} грн
              </div>
              <div style={styles.priceDiscounted}>
                {item.discountedPrice.toFixed(2)} грн
              </div>
            </>
          ) : (
            <div>{item.originalPrice.toFixed(2)} грн</div>
          )}

          <div
            style={{
              fontSize: '16px',
              color: item.paymentStatus === 'completed' ? 'green' : '#888',
              marginLeft: '8px',
            }}
          >
            {item.paymentStatus === 'completed' ? 'Оплачено' : 'Не оплачено'}
          </div>
        </div>

        {!item.delivered && (
          <button
            style={loading ? styles.hideOrderBtnDisabled : styles.hideOrderBtn}
            onClick={() => handleHideOrder(item.id)}
            disabled={loading}
          >
            {loading ? 'Оновлення...' : 'Позначити як доставлено'}
          </button>
        )}
        {item.delivered && (
          <div style={{
            ...styles.deliveryItemStatus,
            color: '#4caf50',
            fontWeight: 'bold',
            marginTop: '8px'
          }}>
            ✅ Замовлення доставлено
          </div>
        )}
      </div>
    </div>
  );
};

export default Delivery;
